import type { ReactNode } from 'react'
import cx from 'classnames'
import styles from './TabBar.module.css'
import { UnreadBadge } from '@/components/UnreadBadge'
import { RawButton } from '@/components/Button'
import { Tooltip } from '@/components/Tooltip'

interface TabBarProps {
  children: ReactNode
  bordered?: boolean
  align?: 'center' | 'left' | 'right'
  stretch?: boolean
  centerText?: boolean
}

/**
 * A row of tabs.
 */
export function TabBar({
  children,
  bordered,
  align,
  stretch = false,
  centerText = false,
}: TabBarProps) {
  return (
    <div
      className={cx(
        styles.root,
        bordered && styles.bordered,
        align && styles[align],
        stretch && styles.stretch,
        centerText && styles.centerText,
      )}
    >
      {children}
    </div>
  )
}

interface TabItemProps {
  active?: boolean
  badgeCount?: number
  badgePosition?: 'top' | 'right'
  children: ReactNode
  onPress?: () => unknown
  to?: string
  cardLayout?: boolean
  highlighted?: boolean
  /**
   * Shown on hover and keyboard focus, like the explanation of an error
   * marker in the label.
   */
  tooltip?: ReactNode
  testId?: string
}

/**
 * A tab in a tab bar, with an optional badge count.
 */
export function TabItem({
  children,
  active,
  badgeCount,
  badgePosition = 'top',
  testId,
  cardLayout,
  highlighted,
  tooltip,
  onPress,
  to,
}: TabItemProps) {
  const tab = (
    <RawButton
      to={to}
      onPress={onPress}
      className={cx(
        styles.tab,
        active && styles.active,
        badgePosition === 'right' && styles.tabRightBadge,
        cardLayout && styles.sidePadding,
      )}
      testId={testId}
    >
      {highlighted && !active && <span className={styles.highlightDot} />}
      <div>{children}</div>
      {badgeCount !== undefined && badgeCount > 0 && (
        <span
          className={
            badgePosition === 'top' ? styles.topBadge : styles.rightBadge
          }
        >
          <UnreadBadge count={badgeCount} />
        </span>
      )}
    </RawButton>
  )
  return tooltip ? <Tooltip content={tooltip}>{tab}</Tooltip> : tab
}
